import { AutoAwesome, KeyboardArrowDown, StarBorder } from '@mui/icons-material'
import {
  Alert,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack
} from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { IRootState } from '../../../../../../../redux/types'
import CustomSwitchComponent from '../../../../../../shared/components/buttons/SwitchCustom'
import Brand from '../../../../../brands/models/BrandInterface'
import Line from '../../../../../brands/models/LineInterface'
import { isClient } from '../../../../utils/RolesHelper'
import { MassivelyAssign } from '../../../hooks/modals/massive-assign/useMassiveAssignState'

interface BrandAndLinesListProps {
  brands: Brand[]
  massivelyAssign: MassivelyAssign
  handleAssignBrandVip: (brand: Brand, checked: boolean) => void
  handleAssignBrand: (brand: Brand, checked: boolean) => void
  handleAssignLine: (line: Line, checked: boolean) => void
  handleAssignLineVip: (line: Line, checked: boolean) => void
}

export function BrandAndLinesList({
  brands,
  massivelyAssign,
  handleAssignBrand,
  handleAssignBrandVip,
  handleAssignLine,
  handleAssignLineVip
}: BrandAndLinesListProps) {
  const { t } = useTranslation(['global'])

  const { user: authUser } = useSelector((state: IRootState) => state.auth)

  const [brandSelectedId, setBrandSelectedId] = useState<number>(0)
  const [lines, setLines] = useState<Line[]>([])

  const openBrandLinesPanel = (brand: Brand, event: any) => {
    if (event.target.type === 'checkbox') return

    if (lines.length > 0 && brandSelectedId === brand.id) {
      setLines([])
      setBrandSelectedId(0)
    } else {
      setLines(brand?.active_lines ?? [])
      setBrandSelectedId(brand.id)
    }
  }

  const isBrandActive = (id: number): boolean => {
    return massivelyAssign.brands.some(brand => brand.brand_id === id)
  }

  const isBrandVIPActive = (id: number): boolean => {
    return massivelyAssign.brands.some(brand => brand.brand_id === id && brand.status === 1)
  }

  const isBrandLineActive = (id: number): boolean => {
    return massivelyAssign.lines.some(line => line.line_id === id)
  }

  const isBrandLineVIPActive = (id: number): boolean => {
    return massivelyAssign.lines.some(line => line.line_id === id && line.status === 1)
  }

  return (
    <Grid container>
      <Grid md={12} xs={12}>
        <Alert severity="info" variant="outlined">
          <StarBorder /> Simple
          <AutoAwesome /> VIP
        </Alert>
      </Grid>
      <Grid md={6} xs={12}>
        <List
          sx={{ bgcolor: 'background.paper' }}
          subheader={
            <ListSubheader component="div" id="brand-list-subheader">
              {t('brands')}
            </ListSubheader>
          }
        >
          {brands.map((brand: Brand) => (
            <ListItem
              onClick={event => openBrandLinesPanel(brand, event)}
              key={`brand-${brand.id}`}
              secondaryAction={
                <Stack direction="row">
                  {isBrandActive(brand.id) && (
                    <Checkbox
                      icon={<StarBorder />}
                      checkedIcon={<AutoAwesome />}
                      checked={isBrandVIPActive(brand.id)}
                      onChange={(_, checked) => handleAssignBrandVip(brand, checked)}
                    />
                  )}
                  <IconButton aria-label="arrow-direction">
                    <KeyboardArrowDown
                      sx={[
                        { mr: -1, transition: '0.2s' },
                        brand?.active_lines?.length === 0 && { opacity: 0 },
                        brandSelectedId === brand.id && { transform: 'rotate(-90deg)' }
                      ]}
                    />
                  </IconButton>
                </Stack>
              }
              disablePadding
            >
              {!isClient(authUser.role_id) && (
                <ListItemButton dense selected={brandSelectedId === brand.id}>
                  <ListItemIcon>
                    <CustomSwitchComponent checked={isBrandActive(brand.id)} onChange={(_, checked) => handleAssignBrand(brand, checked)} />
                  </ListItemIcon>
                  <ListItemText id={`id-brand-${brand.id}`} primary={brand.name} />
                </ListItemButton>
              )}
            </ListItem>
          ))}
        </List>
      </Grid>

      {lines.length > 0 && (
        <Grid md={6} xs={12}>
          <List
            sx={{ bgcolor: 'background.paper' }}
            subheader={
              <ListSubheader component="div" id="line-list-subheader">
                {t('lines')}
              </ListSubheader>
            }
          >
            {lines.map((line: Line) => (
              <ListItem
                key={`line-${line.id}`}
                secondaryAction={
                  isBrandLineActive(line.id) && (
                    <Checkbox
                      icon={<StarBorder />}
                      checkedIcon={<AutoAwesome />}
                      checked={isBrandLineVIPActive(line.id)}
                      onChange={(_, checked) => handleAssignLineVip(line, checked)}
                    />
                  )
                }
                disablePadding
              >
                {!isClient(authUser.role_id) && (
                  <ListItemButton dense>
                    <ListItemIcon>
                      <CustomSwitchComponent
                        checked={isBrandLineActive(line.id)}
                        onChange={(_, checked) => handleAssignLine(line, checked)}
                      />
                    </ListItemIcon>
                    <ListItemText id={`id-line-${line.id}`} primary={line.name} />
                  </ListItemButton>
                )}
              </ListItem>
            ))}
          </List>
        </Grid>
      )}
    </Grid>
  )
}
